/**
 * Pure, date-derived helpers for the Activity redesign. Everything here is
 * computed from data already on the page (timeline events) — NO fabricated
 * scores. The "hero" stats (readiness %, consistency, expert) are deliberately
 * absent because there's no backing metric yet; these cover only what the
 * timestamps + baseline presence honestly support.
 */
import type { TimelineEvent } from '../components/CaptureTimeline';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Recency status. Green ≤14d, amber >14d, grey when there's no (live)
 *  activity at all. Kept coarse on purpose — the dates support this much and
 *  no more. */
export type ParticipantStatus = 'active' | 'idle' | 'dormant';

export interface ParticipantSummary {
  /** ISO of the most recent non-disabled event, or null. */
  lastActivity: string | null;
  /** Non-disabled session count (baselines excluded). */
  sessionCount: number;
  /** Non-disabled baseline count. */
  baselineCount: number;
  hasBaseline: boolean;
  /** Distinct procedures practised, most-recorded first. */
  procedures: { name: string; count: number }[];
  status: ParticipantStatus;
}

function statusFor(lastActivity: string | null, now: Date): ParticipantStatus {
  if (!lastActivity) return 'dormant';
  const age = now.getTime() - new Date(lastActivity).getTime();
  if (Number.isNaN(age)) return 'dormant';
  return age <= 14 * DAY_MS ? 'active' : age <= 30 * DAY_MS ? 'idle' : 'dormant';
}

/**
 * Roll a participant's mixed event stream into a scannable summary. Disabled
 * (soft-deleted) events are ignored throughout — a disabled take is neither
 * recent practice nor a real session for the count.
 */
export function summarizeParticipant(
  events: TimelineEvent[],
  now: Date = new Date(),
): ParticipantSummary {
  const live = events.filter((e) => e.deletedAt === null);
  let lastActivity: string | null = null;
  let sessionCount = 0;
  let baselineCount = 0;
  const procCounts = new Map<string, number>();

  for (const e of live) {
    if (lastActivity === null || e.capturedAt > lastActivity) {
      lastActivity = e.capturedAt;
    }
    if (e.kind === 'session') {
      sessionCount += 1;
      const name = e.procedureName?.trim();
      if (name) procCounts.set(name, (procCounts.get(name) ?? 0) + 1);
    } else {
      baselineCount += 1;
    }
  }

  const procedures = [...procCounts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  return {
    lastActivity,
    sessionCount,
    baselineCount,
    hasBaseline: baselineCount > 0,
    procedures,
    status: statusFor(lastActivity, now),
  };
}

/** "4 days ago", "Today", "3 weeks ago" — compact relative-time for the
 *  last-practice line. Null → "No activity".
 *
 *  Counts CALENDAR days in the viewer's local time, like `feedBucket` and
 *  like the dates on the session cards — not elapsed 24-hour spans. The
 *  elapsed version said "Today" for a recording made 18 hours earlier at
 *  4 PM the previous afternoon, beside a card that said yesterday's date
 *  (2026-09-22). */
export function relativeDay(iso: string | null, now: Date = new Date()): string {
  if (!iso) return 'No activity';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return 'No activity';
  const days = Math.round((startOfDay(now) - startOfDay(then)) / DAY_MS);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  const months = Math.floor(days / 30);
  return months <= 1 ? '1 month ago' : `${months} months ago`;
}

/** Chronological feed buckets, newest first. */
export type FeedBucketKey = 'today' | 'yesterday' | 'week' | 'month' | 'older';

const FEED_BUCKET_LABEL: Record<FeedBucketKey, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  week: 'Earlier this week',
  month: 'Earlier this month',
  older: 'Older',
};

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Human label for a feed bucket key. */
export function feedBucketLabel(key: FeedBucketKey): string {
  return FEED_BUCKET_LABEL[key];
}

/** Which feed section an event falls into, by calendar day. */
export function feedBucket(iso: string, now: Date = new Date()): FeedBucketKey {
  const t = new Date(iso).getTime();
  const today0 = startOfDay(now);
  if (t >= today0) return 'today';
  if (t >= today0 - DAY_MS) return 'yesterday';
  if (t >= today0 - 6 * DAY_MS) return 'week';
  if (t >= today0 - 29 * DAY_MS) return 'month';
  return 'older';
}

const BUCKET_ORDER: FeedBucketKey[] = [
  'today',
  'yesterday',
  'week',
  'month',
  'older',
];

/**
 * Group an already-newest-first event stream into ordered, non-empty feed
 * sections. Preserves the incoming order within each section.
 */
export function groupIntoFeed(
  events: TimelineEvent[],
  now: Date = new Date(),
): { key: FeedBucketKey; label: string; events: TimelineEvent[] }[] {
  const byBucket = new Map<FeedBucketKey, TimelineEvent[]>();
  for (const e of events) {
    const key = feedBucket(e.capturedAt, now);
    const list = byBucket.get(key);
    if (list) list.push(e);
    else byBucket.set(key, [e]);
  }
  return BUCKET_ORDER.filter((k) => byBucket.has(k)).map((k) => ({
    key: k,
    label: FEED_BUCKET_LABEL[k],
    events: byBucket.get(k)!,
  }));
}
